// THE HOSPITAL — where starving citizens go instead of dying in the field (src/game/hungerRules.js has the rules).
// Once the hunger stage reaches 'hospital' (20 min after the last FEED), feedManager's roll admits one citizen at a time: it walks
// to the Hospital building (or a sickbed at home if there is none — scene/peasants.js does the walking), earns nothing and does not
// click while it is there. Each patient is a NAMED citizen whose CONDITION worsens with the wait — mild → serious → severe → terminal
// (HospitalConfig.conditions) — and the player treats them one at a time from the Hospital pop-up (the 🏥 badge over the building,
// the chip, or the message card) at feed rate × healCostMult × the condition's costMult: neglect is dear, the worse the dearer. A
// TERMINAL patient cannot be saved: treating it costs the most and the citizen dies. A patient left for HospitalConfig.stayMs (48 h
// — the clock runs while the game is closed) dies too: a loss like any other (profile.citizenLoss, a grave in the cemetery, the
// Registry's "lost" column).
// Patients are saved with the kingdom: k.hospital = [{ id, goonId, name, at }].

import { Events, on, emit } from '../ui/eventBus.js'
import { HospitalConfig, FeedConfig, GoonTypes, HomeCare, BuildingTypes } from '../config/balance.js'
import { active, persist } from './state.js'
import * as season from './seasonManager.js'
import { patientDue, patientTimeLeftMs, patientCondition, patientHealCost, treatableCost, homeCareMult } from './hungerRules.js'
import { hasBuildingType } from './buildingState.js'
import * as citizens from './citizens.js'

let timer = null

// HOME HEALTHCARE: with no Hospital building every treatment costs HomeCare.healthMult × the base price; owning one brings it to the base.
export const hasHospital = () => hasBuildingType(BuildingTypes.HOSPITAL)
export const careMult = () => homeCareMult(hasHospital(), HomeCare.healthMult)

function list() {
  const k = active()
  if (!Array.isArray(k.hospital)) k.hospital = []
  // every patient is a NAMED individual with an id (a save from before names gets them here)
  const taken = new Set(k.hospital.map((p) => p.name).filter(Boolean)); let changed = false
  for (const p of k.hospital) {
    if (!p.id) { p.id = `pt${p.at}${Math.floor(Math.random() * 1e6)}`; changed = true }
    if (!p.name) { p.name = citizens.patientName(p.goonId, taken); taken.add(p.name); changed = true }
  }
  if (changed) persist()
  return k.hospital
}

// The patients, each with its name, condition (mild / serious / severe / terminal), what treating it costs now and its time left.
export function patients(now = Date.now()) {
  return list().map((p) => { const c = patientCondition(p.at, now, HospitalConfig); return { ...p, condition: c.id, conditionLabel: c.label, fatal: !!c.fatal, cost: patientHealCost(p.at, now, FeedConfig.costPerCitizen, HospitalConfig, careMult()), leftMs: patientTimeLeftMs(p.at, now, HospitalConfig) } })
}
export function count(goonId = null) { return goonId ? list().filter((p) => p.goonId === goonId).length : list().length }
// The price of treating every patient who can be saved (all but the terminal ones) — what "Heal all treatable" charges.
export function healCost(now = Date.now()) { return treatableCost(list().map((p) => p.at), now, FeedConfig.costPerCitizen, HospitalConfig, careMult()) }
export function treatableCount(now = Date.now()) { return list().filter((p) => !patientCondition(p.at, now, HospitalConfig).fatal).length }
// The worst condition among the patients (a cfg.conditions id), or null.
export function worstCondition(now = Date.now()) {
  let w = null
  for (const p of list()) { const c = patientCondition(p.at, now, HospitalConfig), i = HospitalConfig.conditions.indexOf(c); if (w == null || i > w) w = i }
  return w == null ? null : HospitalConfig.conditions[w].id
}
export function timeLeftMs(p, now = Date.now()) { return patientTimeLeftMs(p.at, now, HospitalConfig) }
// The patient closest to dying — for the chip's warning.
export function soonestLeftMs(now = Date.now()) { const l = list(); return l.length ? Math.min(...l.map((p) => timeLeftMs(p, now))) : null }

// Admit one citizen: a random one among those still out in the field (peasants and scribes first; a noble only if nobody else is left).
export function admitOne(now = Date.now()) {
  const free = (id) => Math.max(0, season.getGoonsOwned(id) - count(id))
  const pool = [GoonTypes.PEASANT_CAT, GoonTypes.SCRIBE_CAT].flatMap((id) => Array(free(id)).fill(id))
  const pick = pool.length ? pool[Math.floor(Math.random() * pool.length)] : (free(GoonTypes.NOBLE_CAT) ? GoonTypes.NOBLE_CAT : null)
  if (!pick) return null
  const taken = new Set(list().map((p) => p.name))
  list().push({ goonId: pick, at: now, id: `pt${now}${Math.floor(Math.random() * 1e6)}`, name: citizens.patientName(pick, taken) })
  persist()
  emit(Events.CitizenAdmitted, { goonId: pick })
  emit(Events.HospitalChange, { count: count() })
  return pick
}

// Treat ONE patient at what its condition costs now. A terminal patient cannot be saved: the full price is charged and the citizen
// dies (a headstone in the cemetery). Returns { ok, cost, died, name } — or { ok: false, reason } ('none' | 'coin').
export function treat(id, now = Date.now()) {
  const p = list().find((x) => x.id === id)
  if (!p) return { ok: false, reason: 'none' }
  const c = patientCondition(p.at, now, HospitalConfig)
  const cost = patientHealCost(p.at, now, FeedConfig.costPerCitizen, HospitalConfig, careMult())
  if (season.getWealth() < cost || !season.spendWealth(cost)) return { ok: false, reason: 'coin', cost }
  const k = active()
  k.hospital = list().filter((x) => x.id !== id)
  if (c.fatal) {
    citizens.removeCitizenNamed(p.goonId, p.name)
    season.adjustGoonsOwned(p.goonId, -1, 'died in hospital (treatment failed)')
    persist()
    emit(Events.PatientDied, { goonId: p.goonId, name: p.name, treatment: true, cost })
  } else {
    persist()
    emit(Events.PatientsHealed, { count: 1, cost, name: p.name, goonId: p.goonId })
  }
  emit(Events.HospitalChange, { count: count() })
  return { ok: true, cost, died: !!c.fatal, name: p.name, condition: c.id }
}

// Treat every patient who can be saved, in one payment (healCost()). Terminal patients stay. False if there is nobody to treat or the
// treasury cannot cover it.
export function healAll(now = Date.now()) {
  const l = list(), saved = l.filter((p) => !patientCondition(p.at, now, HospitalConfig).fatal)
  if (!saved.length) return false
  const cost = healCost(now)
  if (season.getWealth() < cost || !season.spendWealth(cost)) return false
  const k = active()
  k.hospital = l.filter((p) => !saved.includes(p))
  persist()
  emit(Events.PatientsHealed, { count: saved.length, cost })
  emit(Events.HospitalChange, { count: count() })
  return true
}

// Patients left too long die. Runs on boot (time away counts) and every minute.
export function tick(now = Date.now()) {
  const l = list()
  const due = l.filter((p) => patientDue(p.at, now, HospitalConfig))
  if (!due.length) return 0
  const k = active()
  k.hospital = l.filter((p) => !patientDue(p.at, now, HospitalConfig))
  for (const p of due) {
    citizens.removeCitizenNamed(p.goonId, p.name)
    season.adjustGoonsOwned(p.goonId, -1, 'died in hospital (neglected)')
    emit(Events.PatientDied, { goonId: p.goonId, name: p.name })
  }
  persist()
  emit(Events.HospitalChange, { count: count() })
  return due.length
}

export function init() {
  if (timer) clearInterval(timer)
  tick()
  timer = setInterval(() => tick(), 60_000)
  on(Events.KingdomChange, () => { tick(); emit(Events.HospitalChange, { count: count() }) })
  on(Events.AccountStatePulled, () => { tick(); emit(Events.HospitalChange, { count: count() }) })
  if (import.meta.env?.DEV && typeof window !== 'undefined') {
    window.__hospital = { patients, count, healCost, healAll, treat, admitOne, tick, worst: worstCondition, treatable: treatableCount, age: (id, ms) => { const p = list().find((x) => x.id === id); if (p) { p.at -= ms; persist() } }, warp: (ms) => { for (const p of list()) p.at -= ms; persist(); tick() } } // dev/test hook (warp: age every patient by ms; age: one patient)
  }
}
